/* eslint-disable relay/unused-fields */
import { type FunctionComponent, type ReactNode } from "react";
import { graphql, useLazyLoadQuery } from "react-relay";

import type { batchQueryAbWidgetQuery } from "scmp-app/queries/__generated__/batchQueryAbWidgetQuery.graphql";

import { reorderEdgesByEntityIds } from "./reorder-edges";

type Props = {
  cftEntityIds: string[];
  cftTopicEntityId?: string;
  children: (data: batchQueryAbWidgetQuery["response"]) => ReactNode;
  spotlightEntityIds: string[];
  storyPackageOneEntityIds: string[];
  storyPackageOneTopicEntityId?: string;
  storyPackageTwoEntityIds: string[];
  storyPackageTwoTopicEntityId?: string;
};

export const AiWidgetBatchQuery: FunctionComponent<Props> = ({
  cftEntityIds,
  cftTopicEntityId,
  children,
  spotlightEntityIds,
  storyPackageOneEntityIds,
  storyPackageOneTopicEntityId,
  storyPackageTwoEntityIds,
  storyPackageTwoTopicEntityId,
}) => {
  const includeStoryPackageOne = storyPackageOneEntityIds.length > 0;
  const includeStoryPackageTwo = storyPackageTwoEntityIds.length > 0;

  const data = useLazyLoadQuery<batchQueryAbWidgetQuery>(
    graphql`
      query batchQueryAbWidgetQuery(
        $storyPackageOneEntityIds: [String!]!
        $storyPackageOneFirst: Int!
        $storyPackageOneTopicEntityId: String
        $withStoryPackageOneTopic: Boolean!
        $includeStoryPackageOne: Boolean!
        $storyPackageTwoEntityIds: [String!]!
        $storyPackageTwoFirst: Int!
        $storyPackageTwoTopicEntityId: String
        $withStoryPackageTwoTopic: Boolean!
        $includeStoryPackageTwo: Boolean!
        $cftEntityIds: [String!]!
        $cftFirst: Int!
        $cftTopicEntityId: String
        $withCftTopic: Boolean!
        $spotlightEntityIds: [String!]!
        $spotlightFirst: Int!
      ) {
        widget1: contents(
          filter: { entityIds: $storyPackageOneEntityIds }
          first: $storyPackageOneFirst
        ) @include(if: $includeStoryPackageOne) {
          edges {
            node {
              ... on Content {
                entityId
                urlAlias
                ...contentCoverImageContent
                  @arguments(withLandscape250x99: true, withSize540x360: true)
                ...homeSecondaryV2ContentItemContent
                ...entityLink
              }
            }
          }
        }
        storyPackageOneTopic: topic(filter: { entityId: $storyPackageOneTopicEntityId })
          @include(if: $withStoryPackageOneTopic) {
          ...entityLink
        }
        widget2: contents(
          filter: { entityIds: $storyPackageTwoEntityIds }
          first: $storyPackageTwoFirst
        ) @include(if: $includeStoryPackageTwo) {
          edges {
            node {
              ... on Content {
                entityId
                urlAlias
                ...contentCoverImageContent
                  @arguments(withSize540x360: true, withSize1200x800: true)
                ...homeSecondaryV2ContentItemContent
                ...entityLink
              }
            }
          }
        }
        storyPackageTwoTopic: topic(filter: { entityId: $storyPackageTwoTopicEntityId })
          @include(if: $withStoryPackageTwoTopic) {
          ...entityLink
        }
        widget3: contents(filter: { entityIds: $cftEntityIds }, first: $cftFirst) {
          edges {
            node {
              ... on Content {
                entityId
                urlAlias
                ...contentCoverImageContent
                  @arguments(withSize1200x800: true, withSize540x360: true)
                ...homeSecondaryV2ContentItemContent
                ...entityLink
              }
            }
          }
        }
        cftTopic: topic(filter: { entityId: $cftTopicEntityId }) @include(if: $withCftTopic) {
          ...entityLink
        }
        widget4: contents(filter: { entityIds: $spotlightEntityIds }, first: $spotlightFirst) {
          edges {
            node {
              ... on Content {
                entityId
                urlAlias
                ...contentCoverImageContent
                  @arguments(withSize1200x800: true, withSize540x360: true)
                ...homeSecondaryV2ContentItemContent
                ...entityLink
              }
            }
          }
        }
      }
    `,
    {
      cftEntityIds,
      cftFirst: cftEntityIds.length,
      cftTopicEntityId: cftTopicEntityId ?? "",
      includeStoryPackageOne,
      includeStoryPackageTwo,
      spotlightEntityIds,
      spotlightFirst: spotlightEntityIds.length,
      storyPackageOneEntityIds,
      storyPackageOneFirst: storyPackageOneEntityIds.length,
      storyPackageOneTopicEntityId: storyPackageOneTopicEntityId ?? "",
      storyPackageTwoEntityIds,
      storyPackageTwoFirst: storyPackageTwoEntityIds.length,
      storyPackageTwoTopicEntityId: storyPackageTwoTopicEntityId ?? "",
      withCftTopic: !!cftTopicEntityId,
      withStoryPackageOneTopic: !!storyPackageOneTopicEntityId,
      withStoryPackageTwoTopic: !!storyPackageTwoTopicEntityId,
    },
    { fetchPolicy: "network-only" },
  );

  const reorderedData = {
    ...data,
    widget1: data.widget1
      ? {
          ...data.widget1,
          edges: reorderEdgesByEntityIds(data.widget1.edges, storyPackageOneEntityIds),
        }
      : data.widget1,
    widget2: data.widget2
      ? {
          ...data.widget2,
          edges: reorderEdgesByEntityIds(data.widget2.edges, storyPackageTwoEntityIds),
        }
      : data.widget2,
    widget3: {
      ...data.widget3,
      edges: reorderEdgesByEntityIds(data.widget3?.edges, cftEntityIds),
    },
    widget4: {
      ...data.widget4,
      edges: reorderEdgesByEntityIds(data.widget4?.edges, spotlightEntityIds),
    },
  };

  return children(reorderedData);
};
